import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./payslip.css";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  ReceiptLong as ReceiptIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchPayslipsSelf } from "./api/teacherHrApi";
import "./teacherHr.css";

const monthOptions = [
  { v: "all", label: "Months" },
  ...Array.from({ length: 12 }, (_, i) => ({
    v: String(i + 1),
    label: new Date(2000, i, 1).toLocaleString("default", { month: "long" }),
  })),
];

const currency = (val) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(val || 0));

const statusChip = (status) => {
  const map = {
    paid: { color: "success", variant: "filled" },
    finalized: { color: "info", variant: "filled" },
    draft: { color: "default", variant: "outlined" },
    pending: { color: "warning", variant: "filled" },
  };
  const cfg = map[status?.toLowerCase()] || {
    color: "default",
    variant: "outlined",
  };
  return (
    <Chip size="small" label={status} color={cfg.color} variant={cfg.variant} />
  );
};

export default function PayslipsList() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!year) return;

    setLoading(true);
    try {
      const params = { year };

      if (month && month !== "all") {
        params.month = Number(month);
      }

      const res = await fetchPayslipsSelf(params);

      const data = res.data?.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(
        e.response?.data?.message || e.message || "Failed to load payslips",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    const m = String(new Date().getMonth() + 1);
    setMonth(m);
  }, []);

  useEffect(() => {
    if (month) {
      load();
    }
  }, [load]);

  const totals = useMemo(() => {
    if (!rows.length) return null;
    return rows.reduce(
      (acc, r) => ({
        count: acc.count + 1,
        gross: acc.gross + Number(r.gross || 0),
        net: acc.net + Number(r.net || 0),
        paid: acc.paid + Number(r.paidAmount || 0),
      }),
      { count: 0, gross: 0, net: 0, paid: 0 },
    );
  }, [rows]);

  return (
    <div
      className="container-2xl p-2 orange-scrollBar"
      style={{ maxHeight: 620, overflow: "auto" }}
    >
      {/* Header */}
      <Box mb={2}>
        <Box display="flex" alignItems="center" gap={1.5} mb={1}>
          <ReceiptIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography
            variant="h5"
            component="h1"
            fontWeight={700}
            color="text.primary"
          >
            My Payslips
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Only finalized and paid slips are visible. Draft payslips become
          available after your school publishes payroll for that cycle.
        </Typography>
      </Box>

      {/* Summary Cards */}
      {totals && (
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))"
          gap={2}
          mb={2}
        >
          {[
            { label: "Payslips", value: totals.count, color: "primary.main" },
            {
              label: "Total Gross",
              value: currency(totals.gross),
              color: "success.main",
            },
            {
              label: "Total Net",
              value: currency(totals.net),
              color: "info.main",
            },
            {
              label: "Total Paid",
              value: currency(totals.paid),
              color: "text.primary",
            },
          ].map((s) => (
            <Card key={s.label} elevation={0} className="tea-stat-card">
              <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  textTransform="uppercase"
                >
                  {s.label}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={s.color}
                  mt={0.5}
                >
                  {s.value}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Main Card */}
      <Card elevation={1} className="tea-hr-card">
        <CardContent>
          {/* Filters Toolbar */}
          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            alignItems="flex-center"
            mb={2}
            pb={2}
            borderBottom={1}
            borderColor="divider"
          >
            <Box display="flex" alignItems="center" gap={1} mr={1}>
              <FilterIcon fontSize="medium" color="action" />
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontWeight={600}
              >
                Filter
              </Typography>
            </Box>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="ps-y">Year</InputLabel>
              <Select
                labelId="ps-y"
                label="Year"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {[0, 1, 2, 3, 4].map((delta) => {
                  const y = currentYear - delta;
                  return (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="ps-m">Month</InputLabel>
              <Select
                labelId="ps-m"
                label="Month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              >
                {monthOptions.map((m) => (
                  <MenuItem key={m.v} value={m.v}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              size="small"
              startIcon={loading ? null : <RefreshIcon />}
              onClick={load}
              disabled={loading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                padding: "8px 12px",
              }}
            >
              {loading ? "Loading…" : "Refresh"}
            </Button>
          </Box>

          {/* Table */}

          <div
            className="orange-scrollBar"
            style={{ maxHeight: 300, overflow: "auto" }}
          >
            <TableContainer>
              <Table size="small" className="tea-table">
                <TableHead>
                  <TableRow>
                    <TableCell width="140" style={{ fontSize: "13px" }}>
                      Period
                    </TableCell>
                    <TableCell width="120" style={{ fontSize: "13px" }}>
                      Status
                    </TableCell>
                    <TableCell align="right" style={{ fontSize: "13px" }}>
                      Gross
                    </TableCell>
                    <TableCell align="right" style={{ fontSize: "13px" }}>
                      Deductions
                    </TableCell>
                    <TableCell align="right" style={{ fontSize: "13px" }}>
                      Net
                    </TableCell>
                    <TableCell align="right" style={{ fontSize: "13px" }}>
                      Paid
                    </TableCell>
                    <TableCell
                      align="right"
                      width="100"
                      style={{ fontSize: "13px" }}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Skeleton width={80} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={60} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={60} sx={{ ml: "auto" }} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={60} sx={{ ml: "auto" }} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={60} sx={{ ml: "auto" }} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={60} sx={{ ml: "auto" }} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={50} sx={{ ml: "auto" }} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <ReceiptIcon
                          sx={{ fontSize: 48, color: "action.disabled", mb: 1 }}
                        />
                        <Typography color="text.secondary" variant="body1">
                          No payslips for this filter.
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          Try selecting a different year or month.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={r._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {r.month}/{r.year}
                          </Typography>
                        </TableCell>
                        <TableCell>{statusChip(r.status)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            {currency(r.gross)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ color: "error.main" }}>
                          <Typography variant="body2" fontWeight={500}>
                            {currency(r.totalDeductions)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {currency(r.net)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color="success.main"
                            fontWeight={600}
                          >
                            {currency(r.paidAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ViewIcon fontSize="small" />}
                            onClick={() => navigate(`/tea-payslip/${r._id}`)}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
